import './ConquestPlanning.scss'
import React from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { Modal } from '../../../../../components/Modal/Modal'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { ReactComponent as ZocksLogo } from '../../../../../assets/images/ZocksIcon-white.svg'
import { IntegrationServiceId, ConquestPlanningEnvironment } from '../../../../../common/integrations'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'

export type ConquestPlanningCredentialsFormValues = {
  environment: ConquestPlanningEnvironment
}

export interface ConquestPlanningCredentialsModalProps {
  show: boolean
  onSave: (data: { environment: ConquestPlanningEnvironment }) => void
  onCancel: () => void
}

const CONQUEST_PLANNING_ENVIRONMENTS: Array<{
  label: string
  value: ConquestPlanningEnvironment
}> = [
  { label: 'Canada', value: 'CDN' },
  { label: 'United States', value: 'USA' },
]

export const ConquestPlanningCredentialsModal: React.FC<ConquestPlanningCredentialsModalProps> = ({ show, onSave, onCancel }) => {
  const defaultValues: ConquestPlanningCredentialsFormValues = {
    environment: 'USA',
  }

  const form = useForm<ConquestPlanningCredentialsFormValues>({ defaultValues })
  const { handleSubmit, reset } = form

  const handleSave = (data: ConquestPlanningCredentialsFormValues) => {
    onSave({
      environment: data.environment,
    })
    reset(defaultValues)
  }

  const handleOnHide = () => {
    reset(defaultValues)
    onCancel()
  }

  const body = (
    <Container>
      <Row className='zocks-pb-2'>
        <Stack direction='horizontal' className='d-flex justify-content-center align-items-center' gap={3}>
          <div className='zocks-bg-color-primary d-flex zocks-p-0.375 justify-content-center align-items-center zocks-border-0.25'>
            <ZocksLogo width='3rem' height='3rem' />
          </div>
          <i className='bi bi-arrow-left-right zocks-conquest-planning-connect-icon'></i>
          <div className='conquest-planning-bg-color-primary d-flex zocks-p-0.375 justify-content-center align-items-center zocks-border-0.25'>
            <IntegrationIcon serviceId={IntegrationServiceId.CONQUEST_PLANNING} fill='white' width='3rem' height='3rem' />
          </div>
        </Stack>
      </Row>

      <Row className='pb-3'>
        <Col>
          <h3 className='d-flex justify-content-center align-items-center'>Connect Conquest Planning</h3>
        </Col>
      </Row>

      <Row className='pb-3'>
        <Col>
          <p className='d-flex justify-content-center align-items-center'>Please select your region.</p>
        </Col>
      </Row>

      <Row>
        <FormProvider {...form}>
          <ConquestPlanningCredentialsForm />
        </FormProvider>
      </Row>
    </Container>
  )

  const footer = (
    <div className='d-flex gap-3'>
      <Button variant='secondary' onClick={onCancel}>
        Cancel
      </Button>
      <Button variant='primary' onClick={handleSubmit(handleSave)}>
        Save
      </Button>
    </div>
  )

  return <Modal show={show} onHide={handleOnHide} header={<></>} body={body} footer={footer} centered={true} />
}

const ConquestPlanningCredentialsForm: React.FC = () => {
  const { control } = useFormContext<ConquestPlanningCredentialsFormValues>()

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      <Row>
        <Col>
          <Controller
            name='environment'
            control={control}
            rules={{ required: 'Please select a region.' }}
            render={({ field, fieldState }) => (
              <>
                <Form.Label>Region</Form.Label>
                <Form.Select id={field.name} {...field} isInvalid={!!fieldState.error}>
                  {CONQUEST_PLANNING_ENVIRONMENTS.map((env) => (
                    <option key={env.value} value={env.value}>
                      {env.label}
                    </option>
                  ))}
                </Form.Select>
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
    </Container>
  )
}
